import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, IconButton, Select, MenuItem, FormControl, Stack, Typography, Autocomplete, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { KeyboardArrowDown as ChevronDownIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { QueryRule, FieldOption } from 'utils/adb-common-util';

export interface QueryBuilderProps {
    title?: string;
    queryPreferenceOptions?: Array<{ label: string; value: string }>;
    defaultQueryPreference?: string;
    sectorOptions: FieldOption[];
    environmentOptions?: FieldOption[];
    locationOptions: FieldOption[];
    teamFlagOptions: FieldOption[];
    onRunQuery: (rules: QueryRule[]) => void;
    showQueryPreference?: boolean;
    initialFilters?: QueryRule[];
    hideComputeType?: boolean;
    isJob?: boolean;
    isSQL?: boolean;
    isTopTenClusterCostJob?: boolean;
    showAutoTermination?: boolean;
    autoTerminationOptions?: FieldOption[];
    hideWorkspaceName?: boolean;
    clusterNameOptions?: FieldOption[];
    warehouseTypeOptions?: FieldOption[];
    warehouseNameOptions?: FieldOption[];
    showClusterName?: boolean;
    showWarehouseName?: boolean;
    showExecutiveName?: boolean;
    showCatalogName?: boolean;
    catalogNameFilterOptions?: FieldOption[];
    userExecutedByOptions?: FieldOption[];
    jobOptions?: FieldOption[];
}

const QueryBuilder: React.FC<QueryBuilderProps & { resultStateOptions?: FieldOption[] }> = ({
    title = 'Query Preferences',
    queryPreferenceOptions = [],
    defaultQueryPreference = '',
    sectorOptions,
    environmentOptions = [],
    locationOptions,
    teamFlagOptions,
    onRunQuery,
    showQueryPreference = true,
    initialFilters = [],
    hideComputeType = false,
    isJob = false,
    isSQL = false,
    isTopTenClusterCostJob = false,
    showAutoTermination = false,
    autoTerminationOptions = [],
    hideWorkspaceName = false,
    clusterNameOptions = [],
    warehouseTypeOptions = [],
    showWarehouseName = false,
    showExecutiveName = false,
    showCatalogName = false,
    showClusterName = false,
    resultStateOptions = [],
    warehouseNameOptions = [],
    catalogNameFilterOptions = [],
    userExecutedByOptions = [],
    jobOptions = []

}) => {
    const theme = useTheme();
    const colors = theme.palette;
    const [result, setResult] = useState<QueryRule[]>([]);
    const environmentFieldLabel = isJob ? 'Status' : 'Compute Type';

    const fieldOptionsMap = {
        'Workspace Name': sectorOptions,
        'Job Name': sectorOptions,
        [isJob ? 'Status' : 'Compute Type']: environmentOptions,
        'Location': locationOptions,
        'Team Flag': teamFlagOptions,
        'Auto Termination': autoTerminationOptions,
        'Cluster Name': clusterNameOptions,
        'Warehouse Type': warehouseTypeOptions,
        'Warehouse Name': warehouseNameOptions,
        'Result State': resultStateOptions,
        'User Executed By': userExecutedByOptions,
        'Catalog Name': catalogNameFilterOptions,
        'Job': jobOptions
    };

    // Initialize with first available option for each field
    const getInitialRules = (): QueryRule[] => {
        if (initialFilters && initialFilters.length > 0) {
            return initialFilters;
        }

        const initialSectorValue = sectorOptions.find(opt => opt.label === 'All')?.value || sectorOptions[0]?.value || 'all';

        if (hideComputeType) {
            return [
                {
                    id: '1',
                    operator: 'AND',
                    field: 'Workspace Name',
                    value: initialSectorValue
                }
            ];
        }

        const initialEnvValue = environmentOptions.find(opt => opt.label === 'All')?.value || environmentOptions[0]?.value || 'all';

        return [
            {
                id: '1',
                operator: 'AND',
                field: 'Workspace Name',
                value: initialSectorValue
            },
            {
                id: '2',
                operator: 'AND',
                field: environmentFieldLabel,
                value: initialEnvValue
            }
        ];
    };

    const [rules, setRules] = useState<QueryRule[]>(getInitialRules());

    const initialFiltersAppliedRef = useRef(false);
    useEffect(() => {
        if (!initialFilters || initialFilters.length === 0) {
            if (initialFiltersAppliedRef.current) {
                setRules(getInitialRules());
                setResult(getInitialRules());
                initialFiltersAppliedRef.current = false;
            }
            return;
        }
        if (initialFiltersAppliedRef.current) return;
        try {
            const valid = initialFilters.every(f => f && typeof f.field === 'string');
            if (valid) {
                setRules(initialFilters);
                setResult(initialFilters);
                initialFiltersAppliedRef.current = true;
            }
        } catch (e) {
            console.error(e);
        }
    }, [initialFilters]);



    const addRule = () => {
        const newRule: QueryRule = {
            id: Date.now().toString(),
            operator: 'AND',
            field: 'Workspace Name',
            value: sectorOptions.find(opt => opt.label === 'All')?.value || sectorOptions[0]?.value || ''
        };
        setRules([...rules, newRule]);
        setResult([...result, newRule]);
    };

    const removeRule = (id: string) => {
        if (rules.length === 1) return;
        setRules(rules.filter(rule => rule.id !== id));
        setResult(result.filter(rule => rule.id !== id));
    };

    const updateRule = (id: string, key: keyof QueryRule, value: string) => {
        setRules(rules.map(rule => {
            if (rule.id === id) {
                if (key === 'field') {
                    const newFieldOptions = fieldOptionsMap[value as keyof typeof fieldOptionsMap] || [];
                    const defaultValue = newFieldOptions.find(opt => opt.label === 'All')?.value || newFieldOptions[0]?.value || '';
                    return { ...rule, [key]: value, value: defaultValue };
                }
                return { ...rule, [key]: value };
            }
            return rule;
        }));
        setResult(result.map(rule => {
            if (rule.id === id) {
                if (key === 'field') {
                    const newFieldOptions = fieldOptionsMap[value as keyof typeof fieldOptionsMap] || [];
                    const defaultValue = newFieldOptions.find(opt => opt.label === 'All')?.value || newFieldOptions[0]?.value || '';
                    return { ...rule, [key]: value, value: defaultValue };
                }
                return { ...rule, [key]: value };
            }
            return rule;
        }));
    };

    const clearAll = () => {
        const clearedRules = getInitialRules();
        setRules(clearedRules);
        setResult(clearedRules);
        initialFiltersAppliedRef.current = false;
    };

    const handleRunQuery = () => {
        const validRules = rules.filter(rule => rule.field && rule.value);
        setResult(validRules);
        onRunQuery(validRules);
    };

    const getValueOptions = (field: string): FieldOption[] => {
        const options = fieldOptionsMap[field as keyof typeof fieldOptionsMap] || [];
        return options;
    };


    // Visual styles shared by the query controls. Query behavior is unchanged.
    const selectSx = {
        height: '32px !important',
        borderRadius: '4px !important',
        backgroundColor: `${colors.DropdownV2.background} !important`,
        color: `${colors.DropdownV2.menuItemText} !important`,
        fontFamily: 'Montserrat',
        fontSize: '14px',
        fontWeight: 500,
        '& .MuiSelect-select': {
            color: `${colors.DropdownV2.menuItemText} !important`,
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: '500 !important',
            py: '5px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent !important',
            borderRadius: '4px !important',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.DropdownV2.mutedText} !important`,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.DropdownV2.borderColorHover} !important`,
        },
        '& .MuiSelect-icon': {
            color: `${colors.DropdownV2.dropdownIconDefault} !important`,
            fill: `${colors.DropdownV2.dropdownIconDefault} !important`,
        },
        '& .MuiSelect-icon path': {
            fill: `${colors.DropdownV2.dropdownIconDefault} !important`,
        },
    };

    const selectMenuProps = {
        PaperProps: {
            sx: {
                borderRadius: '4px',
                backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                color: `${colors.DropdownV2.menuItemText} !important`,
                '& .MuiMenuItem-root': {
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: `${colors.DropdownV2.menuItemText} !important`,
                    '&:hover': {
                        backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                    },
                    '&.Mui-selected': {
                        backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                        color: `${colors.DropdownV2.menuItemHoverText} !important`,
                    },
                },
            },
        },
    };

    // Match the Autocomplete popup items to the existing Select menu styling.
    // All color values continue to come from DropdownV2, just like selectMenuProps.
    const autocompleteOptionSx = {
        fontFamily: 'Montserrat !important',
        fontSize: '14px !important',
        // Keep option text readable in every state; menuItemHoverText can be white.
        color: `${colors.DropdownV2.menuItemText} !important`,
        '& .MuiTypography-root': {
            color: `${colors.DropdownV2.menuItemText} !important`,
        },
        '&:hover': {
            backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
            color: `${colors.DropdownV2.menuItemText} !important`,
        },
        '&.Mui-focused, &.Mui-focusVisible': {
            backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
            color: `${colors.DropdownV2.menuItemText} !important`,
        },
        '&[aria-selected="true"]': {
            backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
            color: `${colors.DropdownV2.menuItemText} !important`,
        },
        '&[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focused': {
            backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
            color: `${colors.DropdownV2.menuItemText} !important`,
        },
    };

    const autocompletePaperSx = {
        borderRadius: '4px',
        backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
        color: `${colors.DropdownV2.menuItemText} !important`,
        '& .MuiAutocomplete-listbox': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
        },
        '&& .MuiAutocomplete-option, && [role="option"]': autocompleteOptionSx,
    };

    return (
        <Box
            sx={{
                backgroundColor: `${colors.queryBuilder.background} !important`,
                color: `${colors.queryBuilder.text} !important`,
                fontFamily: 'Montserrat',
                p: 2,
                borderRadius: '4px',
                mb: 2,
            }}
        >
            <Box
                sx={{
                    pb: 1,
                    mb: 1.5,
                    borderBottom: `1px solid ${colors.queryBuilder.subText}40`,
                }}
            >
                <Typography
                    sx={{
                        color: colors.queryBuilder.text,
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontWeight: 600,
                    }}
                >
                    Advance Filters
                </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{ width: 120 }}>
                    <Button
                        startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                        onClick={clearAll}
                        sx={{
                            color: colors.buttons.primary.disabledText,
                            textTransform: 'none',
                            fontSize: 14,
                            width: '100%',
                            fontWeight: 500,
                            fontFamily: 'Montserrat',
                            justifyContent: 'flex-start',
                            p: 0.5,
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: `${colors.buttons.primary.textColor} !important`,
                            },
                        }}
                    >
                        CLEAR ALL
                    </Button>
                </Box>
                <Typography sx={{ color: colors.queryBuilder.text, fontSize: 14, fontWeight: 500, width: 120, textAlign: 'left' }}>
                    And/Or
                </Typography>
                <Typography sx={{ color: colors.queryBuilder.text, fontSize: 14, fontWeight: 500, flex: 1 }}>
                    Field
                </Typography>
                <Typography sx={{ color: colors.queryBuilder.text, fontSize: 14, fontWeight: 500, flex: 1 }}>
                    Value
                </Typography>
            </Stack>
            <Stack spacing={1}>
                {rules.map((rule, index) => {
                    const valueOptions = getValueOptions(rule.field);
                    return (
                        <Stack
                            key={rule.id}
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                                backgroundColor: `${colors.queryBuilder.tableCellBackground} !important`,
                                borderRadius: '4px',
                                py: 0.5,
                            }}
                        >
                            <Stack direction="row" spacing={1} sx={{ width: 120 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => removeRule(rule.id)}
                                    disabled={rules.length === 1}
                                    sx={{
                                        backgroundColor: 'transparent !important',
                                        color: rules.length === 1 ? colors.buttons.primary.disabledText : `${colors.buttons.primary.deleteButtonBg} !important`,
                                        width: 30,
                                        height: 30,
                                        minWidth: 30,
                                        border: `1px solid ${rules.length === 1 ? colors.buttons.primary.disabledBorder : colors.buttons.primary.deleteButtonBg}`,
                                        borderRadius: '4px',
                                        '& .MuiSvgIcon-root': {
                                            color: rules.length === 1 ? colors.buttons.primary.disabledText : `${colors.buttons.primary.deleteButtonBg} !important`,
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent !important',
                                        },
                                        '&.Mui-disabled': {
                                            color: `${colors.buttons.primary.disabledText} !important`,
                                            borderColor: `${colors.buttons.primary.disabledBorder} !important`,
                                        },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={addRule}
                                    sx={{
                                        backgroundColor: 'transparent !important',
                                        color: `${colors.buttons.primary.borderedText} !important`,
                                        width: 30,
                                        height: 30,
                                        minWidth: 30,
                                        border: `1px solid ${colors.buttons.primary.borderedBorder}`,
                                        borderRadius: '4px',
                                        '&:hover': {
                                            backgroundColor: 'transparent !important',
                                        },
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 20, color: `${colors.buttons.primary.borderedText} !important` }} />
                                </IconButton>
                            </Stack>
                            <FormControl size="small" sx={{ width: 120 }}>
                                <Select
                                    value={rule.operator}
                                    onChange={(e) => updateRule(rule.id, 'operator', e.target.value as 'AND' | 'OR')}
                                    sx={selectSx}
                                    MenuProps={selectMenuProps}
                                    IconComponent={ChevronDownIcon}
                                >
                                    <MenuItem value="AND">AND</MenuItem>
                                    <MenuItem value="OR">OR</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <Select
                                    value={rule.field}
                                    onChange={(e) => updateRule(rule.id, 'field', e.target.value)}
                                    sx={selectSx}
                                    MenuProps={selectMenuProps}
                                    IconComponent={ChevronDownIcon}
                                >
                                    {!hideWorkspaceName && (
                                        <MenuItem value="Workspace Name">Workspace Name</MenuItem>
                                    )}
                                    {isTopTenClusterCostJob && (
                                        <MenuItem value="Job Name">Job Name</MenuItem>
                                    )}
                                    {!hideComputeType && (
                                        <MenuItem value={environmentFieldLabel}>{environmentFieldLabel}</MenuItem>
                                    )}
                                    {showAutoTermination && (
                                        <MenuItem value="Auto Termination">Auto Termination</MenuItem>
                                    )}
                                    {showClusterName && (
                                        <MenuItem value="Cluster Name">Cluster Name</MenuItem>
                                    )}
                                    {isSQL && (
                                        <MenuItem value="Warehouse Type">Warehouse Type</MenuItem>
                                    )}
                                    {showWarehouseName && (
                                        <MenuItem value="Warehouse Name">Warehouse Name</MenuItem>
                                    )}
                                    {showExecutiveName && (
                                        <MenuItem value="User Executed By">User Executed By</MenuItem>
                                    )}
                                    {showCatalogName && (
                                        <MenuItem value="Catalog Name">Catalog Name</MenuItem>
                                    )}
                                    {resultStateOptions && resultStateOptions.length > 0 && (
                                        <MenuItem value="Result State">Result State</MenuItem>
                                    )}
                                    {jobOptions && jobOptions.length > 0 && (
                                        <MenuItem value="Job">Job</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            <Box sx={{ flex: 1 }}>
                                <FormControl size="small" sx={{ width: '100%' }}>
                                    <Autocomplete
                                        options={valueOptions}
                                        size="small"
                                        freeSolo
                                        popupIcon={<ChevronDownIcon />}
                                        forcePopupIcon
                                        getOptionLabel={(opt) =>
                                            typeof opt === 'string' ? opt : opt.label || opt.value || ''
                                        }
                                        value={valueOptions.find((o) => o.value === rule.value) || rule.value || ''}
                                        onChange={(_, newVal) => {
                                            if (typeof newVal === 'string') {
                                                updateRule(rule.id, 'value', newVal);
                                            } else if (newVal) {
                                                updateRule(rule.id, 'value', String((newVal as any).value));
                                            } else {
                                                updateRule(rule.id, 'value', '');
                                            }
                                        }}
                                        inputValue={rule.value || ''}
                                        onInputChange={(_, val) => updateRule(rule.id, 'value', val)}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option.value) === String((value as any)?.value)
                                        }
                                        sx={{
                                            backgroundColor: `${colors.DropdownV2.background} !important`,
                                            borderRadius: '4px !important',
                                            '& .MuiAutocomplete-popupIndicator': {
                                                color: `${colors.DropdownV2.dropdownIconDefault} !important`,
                                            },
                                            '& .MuiAutocomplete-clearIndicator': {
                                                color: `${colors.DropdownV2.menuItemText} !important`,
                                            },
                                        }}
                                        componentsProps={{
                                            paper: { sx: autocompletePaperSx },
                                        }}
                                        ListboxProps={{
                                            sx: {
                                                paddingTop: '0 !important',
                                                paddingBottom: '0 !important',
                                                '&& [role="option"]': autocompleteOptionSx,
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                variant="outlined"
                                                placeholder="Type or select..."
                                                sx={{
                                                    backgroundColor: `${colors.DropdownV2.background} !important`,
                                                    borderRadius: '4px !important',
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: `${colors.DropdownV2.background} !important`,
                                                        color: `${colors.DropdownV2.menuItemText} !important`,
                                                        height: '32px !important',
                                                        borderRadius: '4px !important',
                                                        padding: '0 8px',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'transparent !important',
                                                            borderRadius: '4px !important',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: `${colors.DropdownV2.mutedText} !important`,
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: `${colors.DropdownV2.borderColorHover} !important`,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: `${colors.DropdownV2.menuItemText} !important`,
                                                        fontSize: '14px',
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: '500 !important',
                                                        padding: '6px 4px',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: `${colors.DropdownV2.dropdownIconDefault} !important`,
                                                    },

                                                    '& input::placeholder': {
                                                        color: '#000',
                                                        opacity: 1,
                                                    },
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: {
                                                        backgroundColor: `${colors.queryBuilder.dropdown.background} !important`,
                                                        color: `${colors.queryBuilder.text} !important`,
                                                        borderRadius: '4px !important',
                                                    },
                                                }}
                                            />
                                        )}
                                        noOptionsText="No options"
                                        clearOnEscape
                                        disablePortal
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={autocompleteOptionSx}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: `${colors.DropdownV2.menuItemText} !important`,
                                                        width: '100%',
                                                        fontSize: '14px',
                                                        fontFamily: 'Montserrat',
                                                    }}
                                                >
                                                    {option.label || option.value}
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                </FormControl>
                            </Box>
                        </Stack>
                    );
                })}
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 2,
                    pt: 2,
                    borderTop: `0.5px solid ${colors.queryBuilder.subText}40`,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleRunQuery}
                    sx={{
                        border: `1px solid ${colors.buttons.primary.borderedBorder}`,
                        borderRadius: '4px',
                        color: `${colors.buttons.primary.borderedText} !important`,
                        fontWeight: 500,
                        fontFamily: 'Montserrat',
                        textTransform: 'uppercase',
                        width: 130,
                        height: 31,
                        fontSize: '14px',
                        '&:hover': {
                            borderColor: `${colors.buttons.primary.borderedHover} !important`,
                            color: `${colors.buttons.primary.borderedHoverText} !important`,
                        },
                    }}
                >
                    RUN QUERY
                </Button>
            </Box>
        </Box>
    );
};

export default QueryBuilder;